import React, { useEffect, useState } from 'react';
import { Box, HStack } from 'native-base';
import { faAngleLeft, faBars } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from './Icon';
import Pressable from './Pressable';
import { Caption, H2 } from './Typography';
import routes, { AppRouteProps, useAppNavigate } from '~/pages';
import { useAuthorization } from '~/hooks/auth';
import Loading from './Loading';
import {
    BottomNav,
    resizedUrl,
    useAppSettings,
    useI18n,
    useSpringBigStore,
    useTheme,
    useEmailNotifications,
} from '@springbig/shared';
import { faXmark, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { OpenAppButton } from './GradientButton/OpenAppButton';
import { Button } from '~/shared/Theme';
import Image from '~/shared/Image';
import Debug from '~/shared/Debug';

type TopNavigationProps = {
    toggleMenu: () => void;
    isMenuOpen: boolean;
    isBack?: boolean;
};

const TopNavigation = (props: TopNavigationProps) => {

    const [leftWidth, setLeftWidth] = useState(0);
    const [rightWidth, setRightWidth] = useState(0);

    const {
        merchant,
        member,
        walletId,
        apiEnv,
    } = useSpringBigStore();

    const { location, navigate } = useAppNavigate();

    const {
        isDisabledSideNav,
        merchantOrg,
        playStoreURL,
        appStoreURL,
        profile,
    } = useAppSettings();

    const { isBack } = props;
    const currentPath = location.pathname;
    const goBackRoute = routes.find(route => `/${route.path}` === currentPath)?.goBackPath;
    const navigator = useNavigate();
    const { isLoadingAuth } = useAuthorization();
    const { bottomNav } = useAppSettings();
    const { t } = useI18n();

    const [profilePictureUri, setProfilePictureUri] = useState<string>();

    // Fetch email notifications
    const { data } = useEmailNotifications();
    const hasUnacknowledgedNotifications = (data?.notifications.length || 0) > 0;

    const {
        header_icon_link,
        icon,
        menu_text,
        has_title_text,
        navigation,
        custom_icons,
        header,
        header_text,
        header_icon,
    } = useTheme();

    const [hamburgerIconUrl, setHamburgerIconUrl] = useState<undefined | string>(undefined);

    useEffect(() => {

        if (custom_icons) {

            const hamburgerMenuIconData = custom_icons?.find((e) => e?.key === 'hamburger_icon')?.on;
            const hamburgerMenuHide = hamburgerMenuIconData?.hide_legacy_url;
            const hamburgerMenuUrl = hamburgerMenuIconData?.url;
            if (hamburgerMenuHide === false && hamburgerMenuUrl) {
                setHamburgerIconUrl(hamburgerMenuUrl);
            }
        }
    }, [custom_icons]);

    const [title, setTitle] =
        useState<string | undefined>(undefined);

    const isRouteProps = (item: BottomNav | RouteProps): item is RouteProps => {

        return (item as RouteProps)?.title !== undefined;
    };

    const isBottomNav = (item: BottomNav | RouteProps): item is BottomNav => {

        return (item as BottomNav)?.i18n !== undefined;
    };

    useEffect(() => {

        if (has_title_text) {

            const isSettingsBottomNavInValid = bottomNav.some(obj => obj.path === undefined);
            let _items: (BottomNav | RouteProps)[] = (bottomNav && !isSettingsBottomNavInValid) ? bottomNav : routes.filter(el => el.showTab);

            if (merchant && member) {

                if (!member.subscriptions_enabled) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Subscriptions');
                }

                if (!member.gift_card_enabled) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'SpringbigPay');
                }

                if (!member.show_milestones) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Milestones');
                }

                if (!member.show_rewards) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Rewards');
                }

                if (!member.merchant_website) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Cart');
                }

                if (!member.referrals?.active) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Refer');
                }

                if (!merchant.dutchie_plus_enabled) {
                    _items = _items.filter(_item => ((_item as RouteProps).title || (_item as BottomNav).toScreen) !== 'Shop');
                }
            }

            const bottomNavItem =
                _items.find((_value) => '/' + _value?.path?.replaceAll(':wallet_id', walletId) === currentPath);

            const routesNavItem =
                routes.find((_value) => '/' + _value?.path?.replaceAll(':wallet_id', walletId) === currentPath);


            if (routesNavItem && isRouteProps(routesNavItem)) {
                setTitle(routesNavItem?.title);
            }

            if (bottomNavItem && isBottomNav(bottomNavItem)) {
                const label = bottomNavItem?.customLabel || t(bottomNavItem?.i18n ?? bottomNavItem?.i18n_mobile ?? '');
                setTitle(label);
            }
        }
    }, [navigation, bottomNav, currentPath, has_title_text]);

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    const handleNavigation = (route?: string) => {

        if (isBack) {
            goBackRoute && navigate(goBackRoute);
        }
        else if (route && walletId) {
            navigator(`/w/${walletId}/${route}` as AppRouteProps, {});
        }
    };

    const onLeftIconPress = () => {

        if (header_icon_link) {
            window.open(header_icon_link, '_blank');
            return;
        }

        handleNavigation();
    };

    const mainIcon = () => {

        if (icon?.url) {
            return <img
                src={resizedUrl(icon?.url, 36, 36) }
                style={{ height: '36px' }}
            />;
        }

        return <Caption color={menu_text}>
            { merchant?.name }
        </Caption>;
    };

    useEffect(() => {

        if (member && member.use_custom_profile_picture && member.profile_picture) {
            setProfilePictureUri(member.profile_picture);
        }
        else {
            setProfilePictureUri('');
        }
    }, [member]);


    return (
        <Box
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            px={4}
            h={'60px'}
            w={'100%'}
            bg={header}
            position="relative"
        >
            {/* Left */}
            <Box
                onLayout={(e) => setLeftWidth(e.nativeEvent.layout.width)}
                flexDirection="row"
                alignItems="center"
                flex={1}
            >
                <Pressable
                    onPress={onLeftIconPress}
                    p={'16px'}
                >
                    { goBackRoute
                        ? <Box flexDirection={'row'} alignItems="center" >
                            <SpIcon color={header_text} icon={faAngleLeft} />
                            <Caption
                                color={header_text}
                                fontSize={16}
                                text={'general.back'}
                            />
                        </Box>
                        : mainIcon()
                    }
                </Pressable>
            </Box>
            <Debug />

            {/* Right */}
            <HStack
                onLayout={(e) => setRightWidth(e.nativeEvent.layout.width)}
                alignItems="center"
                space={4}
            >
                { merchantOrg && playStoreURL && appStoreURL ?
                    <OpenAppButton
                        appScheme={merchantOrg + '://'} playStoreURL={playStoreURL} appStoreURL={appStoreURL}
                    />
                    : <></>
                }
                { !!profile.profileIcon &&
                    <Button
                        rounded={'full'}
                        variant={'text'}
                        modifier={'unlocked'}
                        onPress={() => navigate(`w/${walletId}/profile`)}
                        _hover={{ shadow: 5 }}
                    >
                        { profilePictureUri ?
                            <Image w={'36px'} h={'36px'} rounded={'full'} uri={profilePictureUri} />
                            :
                            <SpIcon color={header_icon} icon={faUserCircle} size={36} />
                        }
                    </Button>
                }
                { !isDisabledSideNav &&
                    <Pressable
                        testID={'hamburger-menu'}
                        onPress={props.toggleMenu}
                        p={hamburgerIconUrl ? 0 : '16px'}
                    >
                        { props.isMenuOpen ?
                            <SpIcon
                                color={header_icon}
                                size={24}
                                icon={faXmark}
                            />
                            :
                            // Wrap the hamburger icon with a relative container and add a red dot badge
                            <Box position="relative" display="inline-flex">
                                { hamburgerIconUrl ?
                                    <img
                                        src={hamburgerIconUrl}
                                        width={'38px'}
                                        height={'38px'}
                                        alt="menu"
                                    />
                                    :
                                    <SpIcon
                                        color={menu_text}
                                        size={24}
                                        icon={faBars}
                                    />
                                }
                                {/* Red badge dot in top-right corner - show only if there are unacknowledged notifications */}
                                {hasUnacknowledgedNotifications && (
                                    <Box
                                        position="absolute"
                                        top={-1}
                                        right={-1}
                                        w={'10px'}
                                        h={'10px'}
                                        bg={'red'}
                                        borderRadius={'full'}
                                        borderWidth={2}
                                        borderColor={header}
                                    />
                                )}
                            </Box>
                        }
                    </Pressable>
                }
            </HStack>

            {/* Title */}
            {title && !currentPath.includes('home') && (
                <Box
                    position="absolute"
                    left={0}
                    right={0}
                    alignItems="center"
                >
                    <H2
                        color={header_text}
                        textAlign="center"
                    >
                        {title}
                    </H2>
                </Box>
            )}
        </Box>
    );
};

export default TopNavigation;
